/**
 * **The writes a card makes on the spot** — a labelled row, its choices, and the buttons that send.
 *
 * Owner, 24 September 2026: where a write exists, the card does it where it stands ("Dodaj
 * tydzień", "Zakończ plan", "+2 tygodnie", "Wyślij ponownie"…), instead of sending the trainer to
 * the module. `lib/useTaskAct` holds the rule every one of them follows — nothing optimistic, no
 * second press while the first is in the air, a refusal said in the live region AND in ink right
 * here — and this file is what that looks like.
 *
 * A pending button is `aria-busy`, never `disabled`: a control that disables itself under the
 * trainer's focus drops the keyboard on `<body>` (the same reason `TaskCard`'s primary gives).
 */

import { Link } from '@tanstack/react-router';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, EyeIcon, MailIcon } from '~/shared/ui/AuthIcons';
import { ElapsedMarkIcon, NoteMarkIcon, PlanMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ghost from '~/shared/ui/ghostButton.module.css';
import ink from '~/shared/ui/inkButton.module.css';
import { SendGlyph } from '~/shared/ui/VideoGlyphs';

import type { TaskAct, TaskActRun } from '../lib/useTaskAct';
import type { calendarDestination, TaskAction } from '../model/taskAction';
import {
  CalendarEmptyMark,
  CalendarPlusMark,
  ChatMark,
  CopyMark,
  DumbbellMark,
  FlagMark,
  FormMark,
  PaperPlaneMark,
  PersonMark,
  PlayMark,
  type GlyphProps,
} from './HomeGlyphs';
import styles from './taskBlocks.module.css';

/**
 * The glyph beside an act's word (owner, 25 September 2026): every act on a card reads at a glance
 * as a small line drawing and the word. The word stays the accessible name; the drawing is
 * decorative. One drawing per concept, the app's own where it already has one.
 */
const ACT_GLYPHS = {
  write: ChatMark,
  note: NoteMarkIcon,
  session: DumbbellMark,
  reviewed: EyeIcon,
  play: PlayMark,
  addWeek: CalendarPlusMark,
  shift: SendGlyph,
  resend: PaperPlaneMark,
  extend: ElapsedMarkIcon,
  complete: CheckIcon,
  finish: FlagMark,
  assign: PlanMarkIcon,
  client: PersonMark,
  invitation: MailIcon,
  copy: CopyMark,
  survey: FormMark,
  calendar: CalendarEmptyMark,
} as const satisfies Readonly<Record<string, (props: GlyphProps) => React.ReactElement>>;

export type ActGlyph = keyof typeof ACT_GLYPHS;

/** An act's glyph at the act's size — 16px, in the control's own ink. */
export function ActGlyphMark({ glyph }: { readonly glyph: ActGlyph }) {
  const Mark = ACT_GLYPHS[glyph];
  return <Mark className={styles.actGlyph} />;
}

/**
 * The glyph a task's PRIMARY wears, wherever it is drawn — the desk's bar, the Pulpit's and the
 * Przegląd's card foot — so one act never wears two drawings on two surfaces.
 */
export function actionGlyph(action: TaskAction): ActGlyph {
  switch (action.kind) {
    case 'sessionNote':
      return 'note';
    case 'markReviewed':
      return 'reviewed';
    case 'openChat':
      return 'write';
    case 'openClip':
      return action.intent === 'watch' ? 'play' : 'note';
    case 'openPlans':
      return 'assign';
    case 'openClient':
      return 'client';
    // The roster is where an invitation lives: its invitee is not a client yet (R10).
    case 'openRoster':
      return 'invitation';
    case 'openLead':
      return 'client';
  }
}

export function ActRow({ label, children }: { readonly label?: string; readonly children: ReactNode }) {
  return (
    <div className={styles.acts}>
      {label === undefined ? null : <span className={styles.actsLabel}>{label}</span>}
      {children}
    </div>
  );
}

export function ActSpacer() {
  return <span className={styles.actsSpacer} />;
}

/**
 * An act that goes somewhere rather than writing: the same ghost skin and glyph as a write's
 * button, as a link, so the row reads as one set of acts.
 */
export function ActLink({
  label,
  glyph,
  destination,
}: {
  readonly label: string;
  readonly glyph: ActGlyph;
  readonly destination: ReturnType<typeof calendarDestination>;
}) {
  return (
    <Link className={`${String(ghost.ghost)} ${String(styles.actGhost)} ${String(styles.act)}`} {...destination}>
      <ActGlyphMark glyph={glyph} />
      {label}
    </Link>
  );
}

type ActButtonProps = {
  readonly controller: TaskAct;
  readonly label: string;
  readonly glyph: ActGlyph;
  readonly tone: 'ink' | 'ghost';
} & (
  | {
      /**
       * The write this button sends through `controller`. One that does not take the card away
       * (`closes: false`) says `done` in the button's place once the server accepted it.
       */
      readonly run: TaskActRun;
      readonly onPress?: never;
    }
  | {
      /** A press that sends nothing — it still waits while another act on the card is in the air. */
      readonly onPress: () => void;
      readonly run?: never;
    }
);

export function ActButton({ controller, label, glyph, tone, run, onPress }: ActButtonProps) {
  if (run !== undefined && !run.closes && controller.isDone(run.act)) {
    return <DoneMark label={run.done} />;
  }
  const sending = run !== undefined && controller.isSending(run.act);
  return (
    <button
      type="button"
      className={`${tone === 'ink' ? String(ink.ink) : `${String(ghost.ghost)} ${String(styles.actGhost)}`} ${String(styles.act)}`}
      aria-busy={sending ? 'true' : undefined}
      onClick={() => {
        if (controller.busy) return;
        if (run === undefined) onPress();
        else controller.run(run);
      }}
    >
      <ActGlyphMark glyph={glyph} />
      {label}
    </button>
  );
}

/**
 * What an accepted act that keeps its card says in the button's place.
 *
 * The pressed button is replaced by this mark in the same commit, which drops the keyboard on
 * `<body>`; it lands here instead, once, on mount — and only from `<body>`, so a trainer who has
 * moved on stays where they are.
 */
function DoneMark({ label }: { readonly label: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (document.activeElement === document.body) ref.current?.focus({ preventScroll: true });
  }, []);
  return (
    <span ref={ref} className={styles.doneMark} tabIndex={-1}>
      {label}
    </span>
  );
}

/** One choice among a row's terms. A radio in all but name: `aria-pressed` says which is chosen. */
export function Pick({
  label,
  on,
  onPick,
}: {
  readonly label: string;
  readonly on: boolean;
  readonly onPick: () => void;
}) {
  return (
    <button
      type="button"
      className={`${String(styles.pick)} ${on ? String(styles.pickOn) : ''}`}
      aria-pressed={on}
      onClick={onPick}
    >
      {label}
    </button>
  );
}

/** The refusal, in ink, beside the acts it is about. */
export function ActRefusal({ controller, acts }: { readonly controller: TaskAct; readonly acts: readonly string[] }) {
  const { t } = useTranslation();
  if (!acts.some((act) => controller.wasRefused(act))) return null;
  return <p className={styles.refused}>{t('web:home.live.quickFailed')}</p>;
}

/** The way into the client's conversation on `/czat`, where the canon drew a message field. */
export function ChatLink({ customerId }: { readonly customerId: number | null }) {
  const { t } = useTranslation();
  return (
    <Link className={styles.chat} to="/czat" search={customerId === null ? {} : { klient: customerId }}>
      {t('web:tasks.blocks.chat')}
    </Link>
  );
}
